import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { OfflineBanner } from "@/components/OfflineBanner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthCallback } from "@/components/AuthCallback";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LandingPage } from "@/components/LandingPage";
import { LoginPage } from "@/components/AuthPages";
import AccountDeletionPage from "@/pages/AccountDeletionPage";
import { JoinPage } from "@/pages/JoinPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { ProjectsListPage, CreateProjectPage, ProjectDetailPage } from "@/pages/ProjectsPages";
import { AuditsListPage, CreateAuditPage, AuditDetailPage } from "@/pages/AuditsPages";
import { KaizenListPage, CreateKaizenPage, KaizenDetailPage } from "@/pages/KaizenPages";
import { VSMListPage, CreateVSMPage, VSMDetailPage } from "@/pages/VSMPages";
import { KPIsPage } from "@/pages/KPIsPage";
import { ExpeditionPage } from "@/pages/ExpeditionPage";
import DockplanningPage from "@/pages/DockplanningPage";
import { PickingPage } from "@/pages/PickingPage";
import { MessagesPage } from "@/pages/MessagesPage";
import { UserManagementPage, ExportDataPage } from "@/pages/AdminPages";
import { InvitationsPage } from "@/pages/InvitationsPage";
import { TeamManagementPage } from "@/pages/TeamManagementPage";
import { PlanningV2Page } from "@/pages/PlanningV2Page";
import { ChecklistsListPage, ChecklistDetailPage } from "@/pages/ChecklistsPage";
import { PendingChangesPage, ActivityLogPage } from "@/pages/PendingChangesPage";
import { ChecklistTemplatesPage } from "@/pages/ChecklistTemplatesPage";
import { ArchivePage } from "@/pages/ArchivePage";
import { DamageLogPage } from "@/pages/DamageLogPage";
import { ProcessFlowPage } from "@/pages/ProcessFlowPage";
import { GoalsPage } from "@/pages/GoalsPage";
import { CustomChecklistPage } from "@/pages/CustomChecklistPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { AdminInboxPage } from "@/pages/AdminInboxPage";
import { ChatPage } from "@/pages/ChatPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import SignupPage from "@/pages/SignupPage";
import DemoTour from "@/pages/demo/DemoTour";
import SecurityPage from "@/pages/SecurityPage";
import MethodPage from "@/pages/MethodPage";
import MethodOsPage from "@/pages/method/MethodOsPage";
import BbmiPage from "@/pages/BbmiPage";
import CompanyAdminPage from "@/pages/CompanyAdminPage";
import MasterAdminTenantsPage from "@/pages/MasterAdminTenantsPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import { PaywallModal } from "@/components/PaywallModal";

// Router wrapper to handle auth callback
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/demo" element={<DemoTour />} />
      <Route path="/security" element={<SecurityPage />} />
      {/* Unlisted on purpose — reached from the landing teaser only. */}
      <Route path="/method" element={<MethodPage />} />
      <Route path="/solutions/bbmi-oai" element={<BbmiPage />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/join/:code" element={<JoinPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/account-deletion" element={<AccountDeletionPage />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />

      {/* DMAIC Projects */}
      <Route path="/projects" element={
        <ProtectedRoute module="dmaic">
          <ProjectsListPage />
        </ProtectedRoute>
      } />
      <Route path="/projects/new" element={
        <ProtectedRoute module="dmaic">
          <CreateProjectPage />
        </ProtectedRoute>
      } />
      <Route path="/projects/:id" element={
        <ProtectedRoute module="dmaic">
          <ProjectDetailPage />
        </ProtectedRoute>
      } />

      {/* 6S Audits */}
      <Route path="/audits" element={
        <ProtectedRoute module="audits">
          <AuditsListPage />
        </ProtectedRoute>
      } />
      <Route path="/audits/new" element={
        <ProtectedRoute module="audits">
          <CreateAuditPage />
        </ProtectedRoute>
      } />
      <Route path="/audits/:id" element={
        <ProtectedRoute module="audits">
          <AuditDetailPage />
        </ProtectedRoute>
      } />

      {/* Kaizen Events */}
      <Route path="/kaizen" element={
        <ProtectedRoute module="dmaic">
          <KaizenListPage />
        </ProtectedRoute>
      } />
      <Route path="/kaizen/new" element={
        <ProtectedRoute module="dmaic">
          <CreateKaizenPage />
        </ProtectedRoute>
      } />
      <Route path="/kaizen/:id" element={
        <ProtectedRoute module="dmaic">
          <KaizenDetailPage />
        </ProtectedRoute>
      } />

      {/* Value Stream Mapping */}
      <Route path="/vsm" element={
        <ProtectedRoute module="vsm">
          <VSMListPage />
        </ProtectedRoute>
      } />
      <Route path="/vsm/new" element={
        <ProtectedRoute module="vsm">
          <CreateVSMPage />
        </ProtectedRoute>
      } />
      <Route path="/vsm/:id" element={
        <ProtectedRoute module="vsm">
          <VSMDetailPage />
        </ProtectedRoute>
      } />

      {/* Process Flow */}
      <Route path="/process-flow" element={
        <ProtectedRoute module="vsm">
          <ProcessFlowPage />
        </ProtectedRoute>
      } />

      {/* Goals & OKRs */}
      <Route path="/goals" element={
        <ProtectedRoute module="kpis">
          <GoalsPage />
        </ProtectedRoute>
      } />

      {/* KPIs */}
      <Route path="/kpis" element={
        <ProtectedRoute module="kpis">
          <KPIsPage />
        </ProtectedRoute>
      } />

      {/* Expedition Control */}
      <Route path="/expedition" element={
        <ProtectedRoute module="expedition">
          <ExpeditionPage />
        </ProtectedRoute>
      } />

      <Route path="/dockplanning" element={
        <ProtectedRoute module="dockplanning">
          <DockplanningPage />
        </ProtectedRoute>
      } />

      {/* Picking Control */}
      <Route path="/picking" element={
        <ProtectedRoute module="picking">
          <PickingPage />
        </ProtectedRoute>
      } />

      {/* Damage Log */}
      <Route path="/damage-log" element={
        <ProtectedRoute module="damage_log">
          <DamageLogPage />
        </ProtectedRoute>
      } />

      {/* Custom Checklists */}
      <Route path="/custom-checklists" element={
        <ProtectedRoute module="checklists">
          <CustomChecklistPage />
        </ProtectedRoute>
      } />
      <Route path="/custom-checklists/:id" element={
        <ProtectedRoute module="checklists">
          <CustomChecklistPage />
        </ProtectedRoute>
      } />

      {/* Admin Inbox */}
      <Route path="/admin-inbox" element={
        <ProtectedRoute>
          <AdminInboxPage />
        </ProtectedRoute>
      } />

      {/* Teams */}
      <Route path="/teams" element={
        <ProtectedRoute module="teams">
          <TeamManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/teams/:id" element={
        <ProtectedRoute module="teams">
          <TeamManagementPage />
        </ProtectedRoute>
      } />

      {/* Workforce Planning (legacy) removed — /planning now redirects
          to the unified Planning module at /planning-v2. */}
      <Route path="/planning" element={<Navigate to="/planning-v2" replace />} />

      {/* Workforce Planning v2 (multi-tenant, parallel module) */}
      <Route path="/planning-v2" element={
        <ProtectedRoute module="planning">
          <PlanningV2Page />
        </ProtectedRoute>
      } />

      {/* K.A.N.R.I. Method — available to every tenant */}
      <Route path="/method-os" element={
        <ProtectedRoute>
          <MethodOsPage />
        </ProtectedRoute>
      } />

      {/* Tenant / company admin (non-Smeding) */}
      <Route path="/company-admin" element={
        <ProtectedRoute>
          <CompanyAdminPage />
        </ProtectedRoute>
      } />

      {/* Stripe checkout success polling target */}
      <Route path="/billing/success" element={
        <ProtectedRoute>
          <BillingSuccessPage />
        </ProtectedRoute>
      } />

      {/* Master-admin tenancy panel (modules + billing-transfer queue) */}
      <Route path="/admin/tenants" element={
        <ProtectedRoute>
          <MasterAdminTenantsPage />
        </ProtectedRoute>
      } />

      {/* Checklists */}
      <Route path="/checklists" element={
        <ProtectedRoute module="checklists">
          <ChecklistsListPage />
        </ProtectedRoute>
      } />
      <Route path="/checklists/:id" element={
        <ProtectedRoute module="checklists">
          <ChecklistDetailPage />
        </ProtectedRoute>
      } />

      {/* Archive */}
      <Route path="/archive" element={
        <ProtectedRoute>
          <ArchivePage />
        </ProtectedRoute>
      } />

      {/* Messages */}
      <Route path="/messages" element={
        <ProtectedRoute module="messages">
          <ChatPage />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/users" element={
        <ProtectedRoute requireAdmin>
          <UserManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/invitations" element={
        <ProtectedRoute requireAdmin>
          <InvitationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/export" element={
        <ProtectedRoute requireAdmin>
          <ExportDataPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/pending-changes" element={
        <ProtectedRoute requireAdmin>
          <PendingChangesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/activity-log" element={
        <ProtectedRoute requireAdmin>
          <ActivityLogPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/checklist-templates" element={
        <ProtectedRoute requireAdmin>
          <ChecklistTemplatesPage />
        </ProtectedRoute>
      } />

      {/* Settings - placeholder */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />

      {/* Catch all - redirect to dashboard or landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AppRouter />
          <OfflineBanner />
          <PaywallModal />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
App;
